import { useQueryClient } from "@tanstack/react-query";

import { filterConstants } from "../../constants";
import {
  useGetPageQueryParams,
  useMerchantApiQueryWithParams,
  useQueryString,
  useReactRouterLink,
} from "../../hooks";
import {
  IPageAndSearchRequest,
  IQueryStringNavLinks,
  ITableHeader,
  IViewBankTransferBeneficiary,
  IViewGatewayTransferBeneficiary,
  PagedMeta,
} from "../../interfaces-and-types";
import { View } from "./View";
import { beneficiaryTypes } from "../../enums";

export interface IBeneficiaryViewProps {
  beneficiaryType: beneficiaryTypes;
  data: (IViewBankTransferBeneficiary & IViewGatewayTransferBeneficiary)[];
  headers: ITableHeader[];
  isLoading: boolean;
  isError: boolean;
  totalCount: number;
  queryStringTabs: IQueryStringNavLinks;

  // modals
  openCreateNewModal: () => void;
  openDetailsModal: (data: any) => void;
  refreshTable: () => void;

  // extra buttons
  onExport: () => void;
}

const BENEFICIARY_TYPE_KEY = "benef_type";

export const Beneficiaries = () => {
  const queryClient = useQueryClient();
  const query = useQueryString();
  // const status = query.get(filterConstants.statusKey);
  const beneficiaryType =
    query.get(BENEFICIARY_TYPE_KEY) ?? beneficiaryTypes.bankAccount;
  const { page, page_size, search } = useGetPageQueryParams();

  const { navigateToRoutePreserveParams } = useReactRouterLink();

  const {
    isLoading: isLoadingBeneficiaries,
    isError: isErrorBeneficiaries,
    // isSuccess: isSuccessBeneficiaries,
    data: beneficiaries,
    meta: beneficiariesMeta,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest,
    (IViewBankTransferBeneficiary & IViewGatewayTransferBeneficiary)[],
    PagedMeta
  >([
    beneficiaryType === beneficiaryTypes.gateway
      ? "getGatewayTransferBeneficiaries"
      : "getBankTransferBeneficiaries",
    {
      page: (page && Number(page)) || 1,
      page_size: (page_size && Number(page_size)) || 10,
      search,
      // status: status || "active",
    },
  ]);

  const headers: ITableHeader[] = [
    {
      key: "full_name",
      header: "Alias",
    },
    {
      key: "email",
      header: "Email",
    },
    // {
    //   key: "phone_number",
    //   header: "Phone No",
    // },
    {
      key: "account_name",
      header: "Account Name",
    },
    {
      key: "bank_name",
      header: "Bank Name",
    },
    {
      key: "account_number",
      header: "Account No",
    },
    {
      key: "created_at",
      header: "Date",
    },
    {
      key: "more",
      header: "",
    },
  ];

  const queryStringTabs: IQueryStringNavLinks = {
    query: BENEFICIARY_TYPE_KEY,
    options: [
      {
        key: "Bank",
        value: beneficiaryTypes.bankAccount,
      },
      {
        key: "SwitchApp",
        value: beneficiaryTypes.gateway,
      },
    ],
  };

  const onExport = () => {
    // export data via api and download
    alert("exporting data...");
  };

  const refreshTable = () => {
    queryClient.invalidateQueries([
      "getBeneficiaries",
      {
        page: (page && Number(page)) || 1,
        page_size: (page_size && Number(page_size)) || 10,
        search,
        // status: status || "active",
      },
    ]);
  };

  // insert the functions to open edit/delete modals
  const beneficiariesWithLinks = beneficiaries?.map((p) => ({
    ...p,
    openEditModal: () => navigateToRoutePreserveParams(`${p.id}/edit`),
    openDeleteModal: () => navigateToRoutePreserveParams(`${p.id}/delete`),
  }));

  const openDetailsModal = ({
    id,
    benef_type,
  }: {
    id: string;
    benef_type: beneficiaryTypes;
  }) =>
    navigateToRoutePreserveParams(
      `${
        benef_type === beneficiaryTypes.bankAccount ? "bank" : "gateway"
      }/${id}/details`
    );

  const openCreateNewModal = () => {
    navigateToRoutePreserveParams(`create/bank`);
  };

  return (
    <View
      beneficiaryType={beneficiaryType as beneficiaryTypes}
      data={beneficiaries || []}
      headers={headers}
      isLoading={isLoadingBeneficiaries}
      isError={isErrorBeneficiaries}
      totalCount={beneficiariesMeta?.page_meta.item_count || 0}
      queryStringTabs={queryStringTabs}
      onExport={onExport}
      openCreateNewModal={openCreateNewModal}
      openDetailsModal={openDetailsModal}
      refreshTable={refreshTable}
    />
  );
};
